import React from 'react'
import { TextProps } from 'react-native'

import styled from 'styled-components/native'

interface StyledBaseTextProps {
  color?: string
  align?: 'auto' | 'left' | 'right' | 'center' | 'justify'
}

const StyledBaseText = styled.Text<StyledBaseTextProps>`
  color: ${({ theme, color }) =>
    (color && theme.colors[color as keyof typeof theme.colors]) || theme.colors.text};
  text-align: ${({ align }) => align || 'auto'};
  z-index: 1;
`

export const StyledH1 = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'header',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.header};
  font-size: ${({ theme }) => theme.fonts.fontSize.h1}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.h1}px;
`

export const StyledH2 = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'header',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.header};
  font-size: ${({ theme }) => theme.fonts.fontSize.h2}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.h2}px;
`

export const StyledH3 = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'header',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.header};
  font-size: ${({ theme }) => theme.fonts.fontSize.h3}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.h3}px;
`

export const StyledH4 = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'header',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.header};
  font-size: ${({ theme }) => theme.fonts.fontSize.h4}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.h4}px;
`

export const StyledBodyLargeBold = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.large}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.large}px;
`

export const StyledBodyLarge = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.light};
  font-size: ${({ theme }) => theme.fonts.fontSize.large}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.large}px;
`

export const StyledSubheaderBold = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
`

export const StyledSubheader = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.base};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
`

export const StyledBodyNormal = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.light};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
`

export const StyledBodyNormalBold = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
`

export const StyledBodySmallBold = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.small}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.small}px;
`

export const StyledBodySmall = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.light};
  font-size: ${({ theme }) => theme.fonts.fontSize.small}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.small}px;
`

export const StyledCaptionBold = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.captionBold}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.tinyBold}px;
`

export const StyledCaption = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.base};
  font-size: ${({ theme }) => theme.fonts.fontSize.caption}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.tiny}px;
`

export const StyledButtonText = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.button};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
  letter-spacing: 1.5px;
  text-transform: ${({ theme }) => theme.fonts.textTransform.button};
`

export const StyledLink = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'link',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.button};
  font-size: ${({ theme }) => theme.fonts.fontSize.regular}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
  text-decoration-line: underline;
`

export const StyledLinkSmall = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  font-family: ${({ theme }) => theme.fonts.fontTypes.bold};
  font-size: ${({ theme }) => theme.fonts.fontSize.small}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.small}px;
  text-decoration-line: underline;
`

export const StyledScreenReaderOnly = styled(StyledBaseText).attrs(() => ({
  accessibilityRole: 'text',
}))`
  position: absolute;
  left: -1000000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`

const VariantMapping = {
  h1: StyledH1,
  h2: StyledH2,
  h3: StyledH3,
  h4: StyledH4,
  bodyLargeBold: StyledBodyLargeBold,
  bodyLarge: StyledBodyLarge,
  subheaderBold: StyledSubheaderBold,
  subheader: StyledSubheader,
  bodyNormal: StyledBodyNormal,
  bodyNormalBold: StyledBodyNormalBold,
  bodySmallBold: StyledBodySmallBold,
  bodySmall: StyledBodySmall,
  captionBold: StyledCaptionBold,
  caption: StyledCaption,
  buttonText: StyledButtonText,
  link: StyledLink,
  linkSmall: StyledLinkSmall,
  screenReaderOnly: StyledScreenReaderOnly,
} as const

export type TypographyVariant = keyof typeof VariantMapping

export interface TypographyProps extends Omit<TextProps, 'children'> {
  children?: React.ReactNode
  color?: string
  variant?: TypographyVariant
  align?: 'auto' | 'left' | 'right' | 'center' | 'justify'
}

const Typography = ({
  children,
  color = 'text',
  variant = 'bodyNormal',
  ...rest
}: TypographyProps) => {
  const Component = VariantMapping[variant] as React.ComponentType<StyledBaseTextProps & TextProps>

  if (!Component) {
    return null
  }

  let accessibilityLabel = rest.accessibilityLabel
  if (!accessibilityLabel && typeof children === 'string') {
    accessibilityLabel = children
  }

  // NOTE: The accessibilityRole cannot be overridden when passed as a prop due to styled-components
  // .attrs() behavior. Each styled component above (StyledH1, StyledH2, etc.) defines
  // accessibilityRole in .attrs(() => ({ accessibilityRole: 'header' })), which takes precedence
  // over props passed to the component. This means rest.accessibilityRole will be ignored
  // by the styled component, and the attrs-defined role will always be used instead.
  // @ts-expect-error - attrs property exists at runtime but not in styled-components types
  const accessibilityRole = Component?.attrs?.[0]?.()?.accessibilityRole || 'text'

  return (
    <Component
      color={color}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      {...rest}
    >
      {children}
    </Component>
  )
}

export default Typography
